import React, { useEffect, useMemo, useRef, useState } from 'react';
import { studentAPI, authAPI, homeworkScheduleAPI } from '../services/api';
import { auth } from '../firebase';
import { EmailAuthProvider, reauthenticateWithCredential, updatePassword } from 'firebase/auth';

const BRAND = {
    teal: '#75B9BE',
    charcoal: '#2D4042',
    danger: '#E07A5F'
};

const FONT_OPTIONS = [
    { value: 'OpenDyslexic', fontFamily: "'OpenDyslexic', sans-serif" },
    { value: 'Nunito',       fontFamily: "'Nunito', sans-serif" },
    { value: 'Arial',        fontFamily: 'Arial, sans-serif' },
];

const BG_OPTIONS = [
    { value: '#FDF6EC', label: 'SEN Cream' },
    { value: '#E8F4F8', label: 'Pale Blue' },
    { value: '#FFF0F0', label: 'Soft Peach' },
];

const FontDropdown = ({ value, onChange }) => {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        if (!open) return;
        const handleClickOutside = (e) => {
            if (ref.current && !ref.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [open]);

    const selected = FONT_OPTIONS.find(opt => opt.value === value) || FONT_OPTIONS[0];

    return (
        <div ref={ref} style={{ position: 'relative', userSelect: 'none' }}>
            <style>{`
                @font-face {
                    font-family: 'OpenDyslexic';
                    src: url('https://cdn.jsdelivr.net/npm/open-dyslexic@1.0.3/woff/OpenDyslexic-Regular.woff') format('woff');
                    font-display: swap;
                }
            `}</style>
            <button
                type="button"
                onClick={() => setOpen(prev => !prev)}
                style={{
                    width: '100%',
                    boxSizing: 'border-box',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '12px 16px',
                    border: '2px solid #E0E0E0',
                    borderRadius: '10px',
                    background: '#F8F9FA',
                    cursor: 'pointer',
                    fontFamily: selected.fontFamily,
                    fontSize: '0.95rem',
                    color: BRAND.charcoal,
                    fontWeight: '500',
                    transition: 'all 0.2s',
                    outline: 'none',
                }}
            >
                {selected.value}
                <span style={{ fontSize: '0.75em', marginLeft: '8px', opacity: 0.6 }}>▼</span>
            </button>
            {open && (
                <div style={{
                    position: 'absolute',
                    bottom: 'calc(100% + 4px)',
                    left: 0,
                    right: 0,
                    background: 'white',
                    border: `2px solid ${BRAND.teal}`,
                    borderRadius: '8px',
                    zIndex: 999,
                    overflow: 'hidden',
                    boxShadow: '3px 3px 0px rgba(45,64,66,0.12)',
                }}>
                    {FONT_OPTIONS.map((opt, i) => (
                        <button
                            key={opt.value}
                            type="button"
                            onClick={() => { onChange(opt.value); setOpen(false); }}
                            style={{
                                width: '100%',
                                display: 'block',
                                padding: '10px 12px',
                                border: 'none',
                                borderTop: i > 0 ? '1px solid #E5E7EB' : 'none',
                                background: opt.value === value ? '#EFF9FA' : 'white',
                                cursor: 'pointer',
                                fontFamily: opt.fontFamily,
                                fontSize: '0.95em',
                                color: BRAND.charcoal,
                                fontWeight: opt.value === value ? '700' : '500',
                                textAlign: 'left',
                            }}
                        >
                            {opt.value}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};

const BgDropdown = ({ value, onChange }) => {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        if (!open) return;
        const handleClickOutside = (e) => {
            if (ref.current && !ref.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [open]);

    const selected = BG_OPTIONS.find(opt => opt.value === value) || BG_OPTIONS[0];

    const triggerStyle = {
        width: '100%',
        boxSizing: 'border-box',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '12px 16px',
        border: '2px solid #E0E0E0',
        borderRadius: '10px',
        background: '#F8F9FA',
        cursor: 'pointer',
        fontFamily: 'Inter, sans-serif',
        fontSize: '0.95rem',
        color: BRAND.charcoal,
        fontWeight: '500',
        transition: 'all 0.2s',
        outline: 'none',
    };

    return (
        <div ref={ref} style={{ position: 'relative', userSelect: 'none' }}>
            <button type="button" onClick={() => setOpen(prev => !prev)} style={triggerStyle}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ width: '14px', height: '14px', borderRadius: '3px', background: selected.value, border: '1px solid #D1D5DB', flexShrink: 0 }} />
                    {selected.label}
                </span>
                <span style={{ fontSize: '0.75em', marginLeft: '8px', opacity: 0.6 }}>▼</span>
            </button>
            {open && (
                <div style={{
                    position: 'absolute',
                    bottom: 'calc(100% + 4px)',
                    left: 0,
                    right: 0,
                    background: 'white',
                    border: `2px solid ${BRAND.teal}`,
                    borderRadius: '8px',
                    zIndex: 999,
                    overflow: 'hidden',
                    boxShadow: '3px 3px 0px rgba(45,64,66,0.12)',
                }}>
                    {BG_OPTIONS.map((opt, i) => (
                        <button
                            key={opt.value}
                            type="button"
                            onClick={() => { onChange(opt.value); setOpen(false); }}
                            style={{
                                width: '100%',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                padding: '10px 12px',
                                border: 'none',
                                borderTop: i > 0 ? '1px solid #E5E7EB' : 'none',
                                background: opt.value === value ? '#EFF9FA' : 'white',
                                cursor: 'pointer',
                                fontFamily: 'Inter, sans-serif',
                                fontSize: '0.95em',
                                color: BRAND.charcoal,
                                fontWeight: opt.value === value ? '700' : '500',
                                textAlign: 'left',
                            }}
                        >
                            <span style={{ width: '14px', height: '14px', borderRadius: '3px', background: opt.value, border: '1px solid #D1D5DB', flexShrink: 0 }} />
                            {opt.label}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};

const AccordionSection = ({ id, title, activeTab, setActiveTab, children }) => {
    const isOpen = activeTab === id;
    return (
        <div style={{ border: '2px solid #E5E7EB', borderRadius: '12px', overflow: 'hidden', marginBottom: '10px' }}>
            <button
                onClick={() => setActiveTab(isOpen ? null : id)}
                style={{
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '14px 18px',
                    background: isOpen ? '#F0FDFA' : '#F9FAFB',
                    border: 'none',
                    cursor: 'pointer',
                    fontFamily: 'Nunito, sans-serif',
                    fontWeight: '800',
                    fontSize: '1rem',
                    color: BRAND.charcoal,
                    textAlign: 'left',
                    borderBottom: isOpen ? `2px solid ${BRAND.teal}` : 'none'
                }}
            >
                {title}
                <span style={{ fontSize: '1.1rem', color: BRAND.teal }}>{isOpen ? '▲' : '▼'}</span>
            </button>
            {isOpen && (
                <div style={{ padding: '18px' }}>
                    {children}
                </div>
            )}
        </div>
    );
};

const normaliseCode = (code = '') =>
    code.trim().toUpperCase().replace(/\s+/g, ' ');

const ClassManagementModal = ({ isOpen, onClose, uid }) => {
    const [activeAccountTab, setActiveAccountTab] = useState(null);
    const [students, setStudents] = useState([]);
    const [classes, setClasses] = useState([]);
    const [classDrafts, setClassDrafts] = useState({});
    const [savingClassId, setSavingClassId] = useState(null);
    const [expandedClassId, setExpandedClassId] = useState(null);
    const [loading, setLoading] = useState(false);

    // Password form state
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [passwordLoading, setPasswordLoading] = useState(false);
    const [passwordError, setPasswordError] = useState('');
    const [passwordSuccess, setPasswordSuccess] = useState(false);

    // Default tutorial settings state
    const [defaultFont, setDefaultFont] = useState('OpenDyslexic');
    const [defaultBg, setDefaultBg] = useState('#FDF6EC');
    const [settingsSaving, setSettingsSaving] = useState(false);
    const [settingsError, setSettingsError] = useState('');
    const [settingsSuccess, setSettingsSuccess] = useState(false);

    const fetchData = async () => {
        setLoading(true);
        try {
            const [studentsRes, profileRes, classesRes] = await Promise.all([
                studentAPI.getLibrary().catch(() => ({ data: [] })),
                authAPI.getProfile().catch(() => ({ data: {} })),
                homeworkScheduleAPI.getAll().catch(() => ({ data: [] }))
            ]);
            setStudents(Array.isArray(studentsRes.data) ? studentsRes.data : []);
            const cls = Array.isArray(classesRes.data) ? classesRes.data : [];
            setClasses(cls);
            setClassDrafts(Object.fromEntries(cls.map((c) => [String(c._id), c.name || ''])));
            const user = profileRes.data?.user || {};
            if (user.defaultTutorialFont) setDefaultFont(user.defaultTutorialFont);
            if (user.defaultTutorialBg) setDefaultBg(user.defaultTutorialBg);
            const code = normaliseCode(
                user.registrationCodeUsed || ''
            );
            const cl26 = code === 'CL26';
            // CL26: force and lock anonymous-profile mode + lower reading age
            if (cl26 && uid) {
                localStorage.setItem(`studentProfileMode_${uid}`, 'supply_teacher');
                localStorage.setItem(`readingAgeMode_${uid}`, 'lower');
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (isOpen) {
            fetchData();
        }
    }, [isOpen]);

    const uniqueStudents = useMemo(() => {
        const grouped = new Map();

        students.forEach((student) => {
            const masterId = student.sourceStudentId || student._id;
            const dedupKey = String(masterId);

            if (!grouped.has(dedupKey)) {
                grouped.set(dedupKey, {
                    ...student,
                    allInstanceIds: [String(student._id)]
                });
            } else {
                grouped.get(dedupKey).allInstanceIds.push(String(student._id));
            }
        });

        return Array.from(grouped.values()).sort((a, b) => a.name.localeCompare(b.name));
    }, [students]);

    // Students grouped by their class (homeworkScheduleId is populated by /library).
    const studentsByClass = useMemo(() => {
        const m = new Map();
        for (const s of students) {
            const sched = s.homeworkScheduleId;
            const cid = sched && sched._id ? String(sched._id) : (sched ? String(sched) : null);
            if (!cid) continue;
            if (!m.has(cid)) m.set(cid, []);
            m.get(cid).push(s);
        }
        for (const list of m.values()) list.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        return m;
    }, [students]);

    const handleRenameClass = async (classId, name) => {
        const trimmed = (name || '').trim();
        if (!trimmed) return;
        try {
            setSavingClassId(classId);
            await homeworkScheduleAPI.rename(classId, trimmed);
            setClasses((prev) => prev.map((c) => (String(c._id) === String(classId) ? { ...c, name: trimmed } : c)));
        } catch (err) {
            window.alert('Failed to rename class. Please try again.');
        } finally {
            setSavingClassId(null);
        }
    };

    const handleDeleteStudent = async (studentIds, studentName) => {
        if (!window.confirm(`Permanently delete student "${studentName}" from ALL classes? This cannot be undone.`)) {
            return;
        }

        try {
            await Promise.all(studentIds.map(id => studentAPI.deleteById(id)));
            setStudents(prev => prev.filter(s => !studentIds.includes(String(s._id))));
        } catch (err) {
            alert(err.response?.data?.error || 'Failed to delete student.');
        }
    };

    const handleSaveSettings = async () => {
        setSettingsError('');
        setSettingsSuccess(false);
        setSettingsSaving(true);
        try {
            await authAPI.updateSettings({ defaultTutorialFont: defaultFont, defaultTutorialBg: defaultBg });
            setSettingsSuccess(true);
        } catch (err) {
            setSettingsError(err.response?.data?.error || 'Failed to save settings.');
        } finally {
            setSettingsSaving(false);
        }
    };

    const handleChangePassword = async () => {
        setPasswordError('');
        setPasswordSuccess(false);

        if (!newPassword || !confirmPassword || !currentPassword) {
            setPasswordError('Please fill in all password fields.');
            return;
        }
        if (newPassword !== confirmPassword) {
            setPasswordError('New passwords do not match.');
            return;
        }
        if (newPassword.length < 6) {
            setPasswordError('New password must be at least 6 characters.');
            return;
        }

        setPasswordLoading(true);
        try {
            const user = auth.currentUser;
            const credential = EmailAuthProvider.credential(user.email, currentPassword);
            await reauthenticateWithCredential(user, credential);
            await updatePassword(user, newPassword);
            setPasswordSuccess(true);
            setCurrentPassword('');
            setNewPassword('');
            setConfirmPassword('');
        } catch (err) {
            const code = err.code;
            if (code === 'auth/wrong-password' || code === 'auth/invalid-credential') {
                setPasswordError('Current password is incorrect.');
            } else if (code === 'auth/too-many-requests') {
                setPasswordError('Too many attempts. Please try again later.');
            } else {
                setPasswordError('Failed to update password. Please try again.');
            }
        } finally {
            setPasswordLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(45,64,66,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000 }}>
            <div className="dash-card" style={{ width: '900px', maxWidth: '95%', maxHeight: '90vh', overflowY: 'auto', background: 'white', padding: '22px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                    <h3 style={{ margin: 0, color: BRAND.charcoal }}>My Account</h3>
                    <button className="btn-base" onClick={onClose} style={{ background: '#F0F2F5', color: BRAND.charcoal }}>Close</button>
                </div>

                {/* Tab 1: New Password */}
                <AccordionSection id="newPassword" title="Change Password" activeTab={activeAccountTab} setActiveTab={setActiveAccountTab}>
                    <div style={{ display: 'grid', gap: '14px', maxWidth: '420px' }}>
                        <div>
                            <label style={{ display: 'block', marginBottom: '6px', fontWeight: '700', fontSize: '0.85em', color: '#5D7375', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Current Password</label>
                            <input
                                type="password"
                                className="dash-input"
                                value={currentPassword}
                                onChange={(e) => setCurrentPassword(e.target.value)}
                                placeholder="Enter current password"
                            />
                        </div>
                        <div>
                            <label style={{ display: 'block', marginBottom: '6px', fontWeight: '700', fontSize: '0.85em', color: '#5D7375', textTransform: 'uppercase', letterSpacing: '0.04em' }}>New Password</label>
                            <input
                                type="password"
                                className="dash-input"
                                value={newPassword}
                                onChange={(e) => setNewPassword(e.target.value)}
                                placeholder="Enter new password"
                            />
                        </div>
                        <div>
                            <label style={{ display: 'block', marginBottom: '6px', fontWeight: '700', fontSize: '0.85em', color: '#5D7375', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Confirm New Password</label>
                            <input
                                type="password"
                                className="dash-input"
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                placeholder="Confirm new password"
                            />
                        </div>
                        {passwordError && (
                            <p style={{ margin: 0, color: BRAND.danger, fontSize: '0.9em', fontWeight: '600' }}>{passwordError}</p>
                        )}
                        {passwordSuccess && (
                            <p style={{ margin: 0, color: '#4CAF50', fontSize: '0.9em', fontWeight: '600' }}>Password updated successfully.</p>
                        )}
                        <button
                            className="btn-base btn-teal"
                            style={{ width: 'fit-content' }}
                            onClick={handleChangePassword}
                            disabled={passwordLoading}
                        >
                            {passwordLoading ? 'Updating...' : 'Update Password'}
                        </button>
                    </div>
                </AccordionSection>

                {/* Tab 2: Student Profile Management */}
                <AccordionSection id="studentManagement" title="Student Profile Management" activeTab={activeAccountTab} setActiveTab={setActiveAccountTab}>
                    <p style={{ marginTop: 0, color: '#5D7375', fontSize: '0.92em' }}>
                        View and manage all students across your account. Deleting a student will remove them from all associated classes. This action is permanent.
                    </p>

                    {loading ? (
                        <p style={{ fontStyle: 'italic', color: '#666' }}>Loading students...</p>
                    ) : uniqueStudents.length === 0 ? (
                        <p style={{ fontStyle: 'italic', color: '#888' }}>No students found.</p>
                    ) : (
                        <div style={{ display: 'grid', gap: '8px' }}>
                            {uniqueStudents.map((student) => (
                                <div key={student.allInstanceIds[0]} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px', padding: '12px 14px', border: '1px solid #E5E7EB', borderRadius: '10px', background: '#F8FAFC' }}>
                                    <div style={{ color: '#2D4042' }}>
                                        <strong style={{ fontSize: '1.05em' }}>{student.name}</strong>
                                        <div style={{ color: '#6B7280', fontSize: '0.85em', marginTop: '2px' }}>
                                            Age {student.age} • Reading Age {student.readingAge} • Enrolled in {student.allInstanceIds.length} class{student.allInstanceIds.length !== 1 ? 'es' : ''}
                                        </div>
                                    </div>
                                    <button
                                        className="btn-base"
                                        onClick={() => handleDeleteStudent(student.allInstanceIds, student.name)}
                                        style={{ background: '#FDECEC', color: '#E07A5F', padding: '8px 12px', fontSize: '0.85rem' }}
                                    >
                                        Delete Student
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                </AccordionSection>

                {/* Class Profile Management */}
                <AccordionSection id="classManagement" title="Class Profile Management" activeTab={activeAccountTab} setActiveTab={setActiveAccountTab}>
                    <p style={{ marginTop: 0, color: '#5D7375', fontSize: '0.92em' }}>
                        Rename any of your classes. The new name applies everywhere the class appears.
                    </p>

                    {loading ? (
                        <p style={{ fontStyle: 'italic', color: '#666' }}>Loading classes...</p>
                    ) : classes.length === 0 ? (
                        <p style={{ fontStyle: 'italic', color: '#888' }}>No classes found.</p>
                    ) : (
                        <div style={{ display: 'grid', gap: '8px' }}>
                            {classes.map((c) => {
                                const id = String(c._id);
                                const draft = classDrafts[id] ?? c.name ?? '';
                                const dirty = draft.trim() && draft.trim() !== (c.name || '');
                                const classStudents = studentsByClass.get(id) || [];
                                const expanded = expandedClassId === id;
                                return (
                                    <div key={id} style={{ border: '1px solid #E5E7EB', borderRadius: '10px', background: '#F8FAFC', overflow: 'hidden' }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px', padding: '10px 14px' }}>
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: 1, minWidth: 0 }}>
                                                <input
                                                    value={draft}
                                                    onChange={(e) => setClassDrafts((d) => ({ ...d, [id]: e.target.value }))}
                                                    onKeyDown={(e) => { if (e.key === 'Enter' && dirty) handleRenameClass(id, draft); }}
                                                    style={{ padding: '8px 10px', border: '2px solid #E0E0E0', borderRadius: '8px', fontSize: '0.95em', fontWeight: '700', color: '#2D4042', boxSizing: 'border-box', width: '100%' }}
                                                />
                                                <span style={{ color: '#6B7280', fontSize: '0.8em' }}>{c.yearGroup}{c.subject ? ` • ${c.subject}` : ''}</span>
                                            </div>
                                            <button
                                                className="btn-base btn-teal"
                                                disabled={!dirty || savingClassId === id}
                                                onClick={() => handleRenameClass(id, draft)}
                                                style={{ padding: '8px 14px', fontSize: '0.85rem', opacity: (!dirty || savingClassId === id) ? 0.5 : 1 }}
                                            >
                                                {savingClassId === id ? 'Saving…' : 'Save'}
                                            </button>
                                        </div>
                                        <button
                                            onClick={() => setExpandedClassId(expanded ? null : id)}
                                            style={{ width: '100%', textAlign: 'left', padding: '8px 14px', border: 'none', borderTop: '1px solid #E5E7EB', background: '#EEF2F4', cursor: 'pointer', fontSize: '0.85em', fontWeight: '700', color: '#2D4042' }}
                                        >
                                            {expanded ? '▾' : '▸'} {classStudents.length} student{classStudents.length !== 1 ? 's' : ''}
                                        </button>
                                        {expanded && (
                                            <div style={{ padding: '8px 14px 12px' }}>
                                                {classStudents.length === 0 ? (
                                                    <p style={{ margin: '4px 0', color: '#888', fontStyle: 'italic', fontSize: '0.85em' }}>No students in this class.</p>
                                                ) : (
                                                    <div style={{ display: 'grid', gap: '4px' }}>
                                                        {classStudents.map((s) => (
                                                            <div key={String(s._id)} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px', padding: '6px 10px', background: '#FFFFFF', border: '1px solid #EEF1F3', borderRadius: '8px', fontSize: '0.85em' }}>
                                                                <strong style={{ color: '#2D4042' }}>{s.name}</strong>
                                                                <span style={{ color: '#6B7280', whiteSpace: 'nowrap' }}>Age {s.age} • RA {s.readingAge}</span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </AccordionSection>

                {/* Tab 3: Default Tutorial Settings */}
                <AccordionSection id="defaultTutorialSettings" title="Default Tutorial Settings" activeTab={activeAccountTab} setActiveTab={setActiveAccountTab}>
                    <p style={{ marginTop: 0, color: '#5D7375', fontSize: '0.92em', lineHeight: '1.5' }}>
                        Set global defaults for all new student tutorials. These can still be overridden on a per-student basis when editing individual student scaffolding.
                    </p>
                    <div style={{ display: 'grid', gap: '16px', maxWidth: '420px' }}>
                        <div>
                            <label style={{ display: 'block', marginBottom: '6px', fontWeight: '700', fontSize: '0.85em', color: '#5D7375', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Default Font</label>
                            <FontDropdown value={defaultFont} onChange={(v) => { setDefaultFont(v); setSettingsSuccess(false); }} />
                        </div>
                        <div>
                            <label style={{ display: 'block', marginBottom: '6px', fontWeight: '700', fontSize: '0.85em', color: '#5D7375', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Default Reading Background</label>
                            <BgDropdown value={defaultBg} onChange={(v) => { setDefaultBg(v); setSettingsSuccess(false); }} />
                        </div>
                    </div>
                    <div style={{ marginTop: '20px', display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <button
                            className="btn-base btn-teal"
                            style={{ width: 'fit-content' }}
                            onClick={handleSaveSettings}
                            disabled={settingsSaving}
                        >
                            {settingsSaving ? 'Saving...' : 'Save Defaults'}
                        </button>
                        {settingsError && <p style={{ margin: 0, color: BRAND.danger, fontSize: '0.9em', fontWeight: '600' }}>{settingsError}</p>}
                        {settingsSuccess && <p style={{ margin: 0, color: '#4CAF50', fontSize: '0.9em', fontWeight: '600' }}>Saved successfully.</p>}
                    </div>
                </AccordionSection>

                {/* Curriculum mapping settings */}
                <AccordionSection id="curriculumMapping" title="Curriculum Mapping" activeTab={activeAccountTab} setActiveTab={setActiveAccountTab}>
                    <div style={{ display: 'grid', gap: '16px', maxWidth: '420px' }}>
                        <div>
                            <label style={{ display: 'block', marginBottom: '6px', fontWeight: '700', fontSize: '0.85em', color: '#5D7375', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Curriculum</label>
                            <select
                                defaultValue="national_curriculum_2014"
                                style={{ width: '100%', boxSizing: 'border-box', padding: '12px 16px', border: '2px solid #E0E0E0', borderRadius: '10px', background: '#F8F9FA', fontFamily: 'Inter, sans-serif', fontSize: '0.95rem', color: BRAND.charcoal, fontWeight: '500', outline: 'none', cursor: 'pointer' }}
                            >
                                <option value="national_curriculum_2014">National Curriculum 2014</option>
                            </select>
                        </div>
                    </div>
                </AccordionSection>

            </div>
        </div>
    );
};

export default ClassManagementModal;
